"use client";

import React, { useRef, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Image from "next/image";
import { cn } from "@/lib/utils";
import { useTheme } from "next-themes";
import { UsersSignupVisualTwo } from "./users-signup-visual-2";
import { HeroHeading } from "./hero-heading";
import GradientBorder from "../global-ui/gradient-border";
import { useMediaQuery } from "usehooks-ts";
import { useInView } from "framer-motion";
export function Hero() {
  const [mounted, setMounted] = useState(false);
  const isMobileQuery = useMediaQuery("(max-width: 768px)");
  const parentRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(parentRef, {
    margin: "0px 100px -50px 0px"
  });
  // Only use the media query result after mounting to prevent hydration issues
  const isMobile = mounted ? isMobileQuery : false;
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    resolvedTheme
  } = useTheme();
  const [currentTheme, setCurrentTheme] = useState("dark");
  useEffect(() => {
    setMounted(true);
    setCurrentTheme(resolvedTheme || "dark");
  }, [resolvedTheme]);
  return <div ref={parentRef} className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-gradient-to-b from-neutral-50 to-transparent px-6 py-16  dark:from-[#09090B] dark:to-[#09090B] md:px-12 md:pb-20" data-sentry-component="Hero" data-sentry-source-file="hero.tsx">
      <BackgroundGrids data-sentry-element="BackgroundGrids" data-sentry-source-file="hero.tsx" />
      <CollisionMechanism beamOptions={{
      initialX: -400,
      translateX: 600,
      duration: 7,
      repeatDelay: 3
    }} containerRef={containerRef} parentRef={parentRef} data-sentry-element="CollisionMechanism" data-sentry-source-file="hero.tsx" />
      <CollisionMechanism beamOptions={{
      initialX: -200,
      translateX: 800,
      duration: 4,
      repeatDelay: 3
    }} containerRef={containerRef} parentRef={parentRef} data-sentry-element="CollisionMechanism" data-sentry-source-file="hero.tsx" />
      <CollisionMechanism beamOptions={{
      initialX: 200,
      translateX: 1200,
      duration: 5,
      repeatDelay: 3
    }} containerRef={containerRef} parentRef={parentRef} data-sentry-element="CollisionMechanism" data-sentry-source-file="hero.tsx" />
      <CollisionMechanism containerRef={containerRef} parentRef={parentRef} beamOptions={{
      initialX: 400,
      translateX: 1400,
      duration: 6,
      repeatDelay: 3
    }} data-sentry-element="CollisionMechanism" data-sentry-source-file="hero.tsx" />

      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-0 md:grid-cols-5 lg:px-20">
        <div className="md:col-span-3">
          <header className="relative z-20 mx-auto mb-2 mt-10 max-w-4xl text-balance md:mb-0 md:text-start">
            <HeroHeading data-sentry-element="HeroHeading" data-sentry-source-file="hero.tsx" />
          </header>
        </div>
        <div className="z-[10] md:col-span-2">
          <UsersSignupVisualTwo isInView={isInView} data-sentry-element="UsersSignupVisualTwo" data-sentry-source-file="hero.tsx" />
        </div>
      </div>

      {/* Bottom Fade Overlay */}
      {/* <div className="pointer-events-none absolute inset-x-0 bottom-0 left-0 z-50 h-20 w-full bg-gradient-to-t from-transparent to-transparent dark:from-[#09090B] dark:via-black dark:to-black" /> */}

      {/* <TopHeroSection /> */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.4,
      delay: 0.9,
      ease: "easeOut"
    }} className="relative mx-auto mt-0 h-full w-full max-w-7xl rounded-[32px] bg-transparent p-4 sm:p-10 md:p-20" data-sentry-element="unknown" data-sentry-source-file="hero.tsx">
        {/* Top Fade Overlay */}
        <div className="pointer-events-none absolute inset-x-0 left-0 top-0 z-0 h-20 w-full bg-gradient-to-b from-transparent via-transparent to-transparent dark:from-[#09090B]" />

        <GradientBorder className="relative flex w-full items-center justify-center rounded-[32px]" customInset="-150%" backgroundOpacity={currentTheme === "dark" ? 1 : 0} backgroundColor="#09090B" dotSpacing={"loose"} intensity={currentTheme === "dark" ? "ultra" : "low"} borderOpacity={currentTheme === "dark" ? 1 : 0} disableAnimations={isMobile || !isInView}
      // pauseAnimation={true}
      // pausePosition={180}
      //   debugMode={true}
      data-sentry-element="GradientBorder" data-sentry-source-file="hero.tsx">
          <div className="flex w-full items-center justify-center rounded-[32px] border border-neutral-200 bg-white p-1 dark:border-neutral-700 dark:bg-black sm:p-2">
            <Image src={resolvedTheme === "dark" ? "https://viral.devassets.app/virallaunch-hero-image-dark.webp" : "https://viral.devassets.app/virallaunch-hero-image-light.webp"} alt="header" width={1920} height={1080} className="h-auto w-full rounded-[24px] object-contain" data-sentry-element="Image" data-sentry-source-file="hero.tsx" />
          </div>
        </GradientBorder>
      </motion.div>
    </div>;
}
const BackgroundGrids = () => {
  return <div className="pointer-events-none absolute inset-0 z-[5] grid h-full w-full -rotate-45 transform select-none grid-cols-2 gap-10 opacity-20 dark:opacity-10 md:grid-cols-4" data-sentry-component="BackgroundGrids" data-sentry-source-file="hero.tsx">
      <div className="relative h-full w-full">
        <GridLineVertical className="left-0" data-sentry-element="GridLineVertical" data-sentry-source-file="hero.tsx" />
        <GridLineVertical className="left-auto right-0" data-sentry-element="GridLineVertical" data-sentry-source-file="hero.tsx" />
      </div>
      <div className="relative h-full w-full">
        <GridLineVertical className="left-0" data-sentry-element="GridLineVertical" data-sentry-source-file="hero.tsx" />
        <GridLineVertical className="left-auto right-0" data-sentry-element="GridLineVertical" data-sentry-source-file="hero.tsx" />
      </div>
      <div className="relative h-full w-full bg-gradient-to-b from-transparent via-neutral-100 to-transparent dark:from-neutral-900">
        <GridLineVertical className="left-0" data-sentry-element="GridLineVertical" data-sentry-source-file="hero.tsx" />
        <GridLineVertical className="left-auto right-0" data-sentry-element="GridLineVertical" data-sentry-source-file="hero.tsx" />
      </div>
      <div className="relative h-full w-full">
        <GridLineVertical className="left-0" data-sentry-element="GridLineVertical" data-sentry-source-file="hero.tsx" />
        <GridLineVertical className="left-auto right-0" data-sentry-element="GridLineVertical" data-sentry-source-file="hero.tsx" />
      </div>
    </div>;
};
const CollisionMechanism = React.forwardRef<HTMLDivElement, {
  containerRef: React.RefObject<HTMLDivElement>;
  parentRef: React.RefObject<HTMLDivElement>;
  beamOptions?: {
    initialX?: number;
    translateX?: number;
    initialY?: number;
    translateY?: number;
    rotate?: number;
    className?: string;
    duration?: number;
    delay?: number;
    repeatDelay?: number;
  };
}>(({
  parentRef,
  containerRef,
  beamOptions = {}
}, ref) => {
  // Ref for the animated beam element.
  const beamRef = useRef<HTMLDivElement>(null);
  // State to manage collision status and coordinates.
  const [collision, setCollision] = useState<{
    detected: boolean;
    coordinates: {
      x: number;
      y: number;
    } | null;
  }>({
    detected: false,
    coordinates: null
  });
  // Key to force re-render and restart the beam animation.
  const [beamKey, setBeamKey] = useState(0);
  // State to ensure collision is detected only once per animation cycle.
  const [cycleCollisionDetected, setCycleCollisionDetected] = useState(false);
  useEffect(() => {
    // This effect is responsible for detecting collisions between the beam and the container.
    const checkCollision = () => {
      if (beamRef.current && containerRef.current && parentRef.current && !cycleCollisionDetected // Only check if a collision hasn't already been detected in the current cycle.
      ) {
        const beamRect = beamRef.current.getBoundingClientRect();
        const containerRect = containerRef.current.getBoundingClientRect();
        const parentRect = parentRef.current.getBoundingClientRect();

        // Collision is detected if the bottom of the beam touches or crosses the top of the container.
        if (beamRect.bottom >= containerRect.top) {
          // Calculate collision coordinates relative to the parent container.
          const relativeX = beamRect.left - parentRect.left + beamRect.width / 2;
          const relativeY = beamRect.bottom - parentRect.top;
          setCollision({
            detected: true,
            coordinates: {
              x: relativeX,
              y: relativeY
            }
          });
          setCycleCollisionDetected(true); // Mark collision as detected for this cycle.
          // Hide the beam immediately upon collision.
          if (beamRef.current) {
            beamRef.current.style.opacity = "0";
          }
        }
      }
    };

    // Check for collision at a regular interval.
    const animationInterval = setInterval(checkCollision, 50);

    // Cleanup interval on component unmount or when dependencies change.
    return () => clearInterval(animationInterval);
  }, [cycleCollisionDetected, containerRef]); // Ensuring this line is correct

  useEffect(() => {
    // This effect handles the aftermath of a collision.
    if (collision.detected && collision.coordinates) {
      // After a delay, reset collision state and make the beam visible again.
      setTimeout(() => {
        setCollision({
          detected: false,
          coordinates: null
        });
        setCycleCollisionDetected(false);
        // Make the beam visible for the next animation cycle.
        if (beamRef.current) {
          beamRef.current.style.opacity = "1"; // Restoring beam opacity
        }
      }, 2000); // Delay before resetting collision state.

      // Reset the beam animation by changing its key, forcing a re-render.
      // This happens after the same delay, ensuring the explosion animation (if any) has time to play.
      setTimeout(() => {
        setBeamKey(prevKey => prevKey + 1);
      }, 2000); // Delay before restarting beam animation.
    }
  }, [collision]);
  return <>
      {/* The animated beam element.
          - `key={beamKey}`: Changing this key forces Framer Motion to re-run the animation from its initial state.
          - `ref={beamRef}`: Used to get the beam's position for collision detection.
          - `animate="animate"`: Defines the target animation state.
          - `initial`: Defines the starting state of the animation.
            - `translateY`: Initial vertical position. Defaults to "-200px" (above the viewport).
            - `translateX`: Initial horizontal position. Defaults to "0px".
            - `rotate`: Initial rotation. Defaults to -45 degrees.
          - `variants`: Defines the animation states.
            - `animate`: The target state for the animation.
              - `translateY`: Target vertical position. Defaults to "800px" (moves downwards).
              - `translateX`: Target horizontal position. Defaults to "700px" (moves to the right).
              - `rotate`: Target rotation (remains -45 degrees by default).
          - `transition`: Defines how the animation transitions between states.
            - `duration`: How long the animation takes to complete one cycle. Defaults to 8 seconds.
            - `repeat: Infinity`: Makes the animation loop forever.
            - `repeatType: "loop"`: The type of repeat. "loop" restarts the animation from the beginning.
            - `ease: "linear"`: The easing function for the animation (constant speed).
            - `delay`: Delay before the animation starts for the first time. Defaults to 0.
            - `repeatDelay`: Delay before the animation repeats after completing a cycle. Defaults to 0.
       */}
      <motion.div key={beamKey} ref={beamRef} animate="animate" initial={{
      translateY: beamOptions.initialY || "-200px",
      translateX: beamOptions.initialX || "0px",
      rotate: beamOptions.rotate || -45
    }} variants={{
      animate: {
        translateY: beamOptions.translateY || "800px",
        translateX: beamOptions.translateX || "700px",
        rotate: beamOptions.rotate || -45
      }
    }} transition={{
      duration: beamOptions.duration || 8,
      repeat: Infinity,
      repeatType: "loop",
      ease: "linear",
      delay: beamOptions.delay || 0,
      repeatDelay: beamOptions.repeatDelay || 0
    }} className={cn("absolute left-96 top-20 z-[5] m-auto h-14 w-px rounded-full bg-gradient-to-t from-orange-500 via-yellow-500 to-transparent opacity-50 dark:opacity-30", beamOptions.className)} />
      <AnimatePresence>
        {collision.detected && collision.coordinates && <Explosion key={`${collision.coordinates.x}-${collision.coordinates.y}`} className="" style={{
        left: `${collision.coordinates.x + 20}px`,
        top: `${collision.coordinates.y}px`,
        transform: "translate(-50%, -50%)"
      }} />}
      </AnimatePresence>
    </>;
});
CollisionMechanism.displayName = "CollisionMechanism";

// The Explosion component renders a visual effect of particles scattering from a central point.
const Explosion = ({
  ...props
}: React.HTMLProps<HTMLDivElement>) => {
  // Generate an array of 20 particle objects, each with a unique ID and random direction.
  const spans = Array.from({
    length: 20
  }, (_, index) => ({
    id: index,
    initialX: 0,
    // All particles start at the center of the Explosion component.
    initialY: 0,
    // `directionX`: Random horizontal direction and distance (between -40 and 40).
    directionX: Math.floor(Math.random() * 80 - 40),
    // `directionY`: Random upward vertical direction and distance (between -60 and -10).
    directionY: Math.floor(Math.random() * -50 - 10)
  }));
  return <div {...props} className={cn("absolute z-50 h-2 w-2", props.className)} data-sentry-component="Explosion" data-sentry-source-file="hero.tsx">
      {/* This motion.div creates a central glowing line effect for the explosion.
          - `initial={{ opacity: 0 }}`: Starts invisible.
          - `animate={{ opacity: [0, 1, 0] }}`: Fades in and then fades out.
          - `exit={{ opacity: 0 }}`: Fades out when the component is removed (due to AnimatePresence in parent).
          - `transition={{ duration: 1, ease: "easeOut" }}`: Animation duration and easing.
       */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: [0, 1, 0]
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 1,
      ease: "easeOut"
    }} className="absolute -inset-x-10 top-0 m-auto h-[4px] w-10 rounded-full bg-gradient-to-r from-transparent via-orange-500 to-transparent blur-sm" data-sentry-element="unknown" data-sentry-source-file="hero.tsx"></motion.div>
      {/* Map over the `spans` array to render each particle as a `motion.span`. */}
      {spans.map(span => <motion.span key={span.id}
    // `initial`: Particle starts at (0,0) relative to the Explosion component's position, and fully opaque.
    initial={{
      x: span.initialX,
      y: span.initialY,
      opacity: 1
    }}
    // `animate`: Particle moves to its calculated random `directionX` and `directionY`, and fades to transparent.
    animate={{
      x: span.directionX,
      y: span.directionY,
      opacity: 0
    }}
    // `transition`: Each particle has a random duration for a more natural explosion effect.
    transition={{
      duration: Math.random() * 1.5 + 0.5,
      ease: "easeOut"
    }} className="absolute h-1 w-1 rounded-full bg-gradient-to-b from-orange-500 to-yellow-500" />)}
    </div>;
};
const GridLineVertical = ({
  className,
  offset
}: {
  className?: string;
  offset?: string;
}) => {
  return <div style={{
    "--background": "#ffffff",
    "--color": "rgba(0, 0, 0, 0.2)",
    "--height": "5px",
    "--width": "1px",
    "--fade-stop": "90%",
    "--offset": offset || "150px",
    //-100px if you want to keep the line inside
    "--color-dark": "rgba(255, 255, 255, 0.3)",
    maskComposite: "exclude"
  } as React.CSSProperties} className={cn("absolute top-[calc(var(--offset)/2*-1)] h-[calc(100%+var(--offset))] w-[var(--width)]", "bg-[linear-gradient(to_bottom,var(--color),var(--color)_50%,transparent_0,transparent)]", "[background-size:var(--width)_var(--height)]", "[mask:linear-gradient(to_top,var(--background)_var(--fade-stop),transparent),_linear-gradient(to_bottom,var(--background)_var(--fade-stop),transparent),_linear-gradient(black,black)]", "[mask-composite:exclude]", "z-30", "dark:bg-[linear-gradient(to_bottom,var(--color-dark),var(--color-dark)_50%,transparent_0,transparent)]", className)} data-sentry-component="GridLineVertical" data-sentry-source-file="hero.tsx"></div>;
};